import React, { useEffect, useRef } from 'react';
import { useGetNoteVersions } from '@/api/notes/useGetNoteVersions';
import { formatSavedDate } from '@/utils/formatDate';
import { Spinner } from '@/components/ui';
import { useNoteContext } from '@/components/notes/context/NoteContext';
import { usePlanInfo } from '@/hooks/usePlanInfo';

interface VersionsPanelProps {
  noteId: string | null;
  isOpen: boolean;
  onClose: () => void;
  /** Incremented by the parent when a new version is created — triggers a panel refresh. */
  refreshKey?: number;
  /** The content row ID currently being viewed (highlights the active row). */
  viewingContentId?: number | null;
  /** Called when the user clicks "Open" on a history row. */
  onOpenVersion?: (contentId: number) => void;
}

const VersionsPanel: React.FC<VersionsPanelProps> = ({
  noteId,
  isOpen,
  onClose,
  refreshKey,
  viewingContentId = null,
  onOpenVersion,
}) => {
  const { onBackToLatest } = useNoteContext();
  const planInfo = usePlanInfo();

  // Pass null when closed so the hook doesn't fetch while hidden
  const { versions, loading, hasMore, loadMore, reset, refresh } = useGetNoteVersions(
    isOpen ? noteId : null,
  );

  // Clear list when panel closes so next open always starts fresh
  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh when the parent signals that a new version was created, but only if open
  useEffect(() => {
    if (refreshKey === undefined || refreshKey === 0) return;
    if (isOpen) refresh();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sentinel element at the bottom of the list triggers loadMore via IntersectionObserver
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div
      className="flex-shrink-0 relative overflow-hidden transition-[width] duration-300 ease-in-out border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
      style={{ width: isOpen ? '320px' : '0' }}
    >
      {/* absolute fill so the flex column has a real height to work against */}
      <div className="absolute inset-0 flex flex-col min-w-[320px]">

        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Versions
            </span>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              aria-label="Close versions panel"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Version list */}
        <div className="flex-1 overflow-y-auto scrollbar-osx min-h-0">
          {versions.length === 0 && !loading && (
            <p className="px-4 py-8 text-sm text-center text-gray-400 dark:text-gray-500">
              No versions yet
            </p>
          )}

          {versions.map((v) => {
            const isActive = v.id === viewingContentId;
            return (
              <div
                key={v.id}
                className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 transition-colors ${
                  isActive
                    ? 'bg-amber-50 dark:bg-amber-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                }`}
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-gray-700 dark:text-gray-200 truncate">
                      {formatSavedDate(v.created_at)}
                    </span>
                    {v.status === 'draft' && (
                      <span className="flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                        Draft
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                    {v.user_name}
                  </span>
                </div>
                <button
                  onClick={() => onOpenVersion?.(v.id)}
                  className={`ml-3 flex-shrink-0 text-xs px-2.5 py-1 rounded border transition-colors ${
                    isActive
                      ? 'border-amber-500 bg-amber-500 text-white dark:border-amber-400 dark:bg-amber-400 dark:text-gray-900 cursor-default'
                      : 'border-gray-300 dark:border-gray-500 text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:text-blue-400'
                  }`}
                >
                  {isActive ? 'Viewing' : 'Open'}
                </button>
              </div>
            );
          })}

          {/* Sentinel — IntersectionObserver target for infinite scroll */}
          <div ref={sentinelRef} className="h-px" />

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-center py-4">
              <Spinner size={20} enableTheme />
            </div>
          )}

          {/* Plan limit info banner */}
          {!hasMore && !loading && versions.length >= planInfo.limits.maxVersionsPerNote && (
            <div className="mx-3 mb-3 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Version history is limited to {planInfo.limits.maxVersionsPerNote} versions on the {planInfo.planName} plan.{' '}
                <a
                  href={planInfo.accountUsageUrl}
                  className="underline font-medium hover:text-blue-900 dark:hover:text-blue-100"
                >
                  View plan
                </a>
              </p>
            </div>
          )}

          {/* End-of-list message */}
          {!hasMore && !loading && versions.length > 0 && (
            <p className="px-4 py-4 text-xs text-center text-gray-400 dark:text-gray-500">
              All versions loaded
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VersionsPanel;
